/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Divider, Typography, CircularProgress, Button, Accordion, AccordionSummary, AccordionDetails, Tab, Tooltip } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useTheme } from '@mui/material/styles'
import { type SubmitHandler, useForm, FormProvider, useFieldArray, type Resolver } from 'react-hook-form'
import GenericButton from '../../../components/GenericButton'
import { type TruncatedDivision, type DivisionAndSection, type Role } from '../../../models/Role'
import AddIcon from '@mui/icons-material/Add'
import { ButtonSize, ButtonType } from '../../../components/GenericButton/styles'
import { grey, shade } from '../../../colors'
import FormInput from '../../../components/FormElements/FormInput'
import DefaultModalActions from '../../../components/GenericModal/DefaultModalActions'
import { UpdateFacultyAndStaffApi, createFacultyAndStaffApi, getFacultyAndStaffApi } from '../../../store/slices/facultyAndStaffSlice/apis'
import { useAppDispatch } from '../../../store'
import { actionLoading } from '../../../store/slices/facultyAndStaffSlice/selectors'
import { notification, flashNotification } from '../../../components/Notifications'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useMutation, useQuery } from '@tanstack/react-query'
import api from '../../../utils/api'
import { getDirtyValues } from '../../../utils/onlyUpdatedFields'
import RoleCard from '../RoleCard'
import ValidationAlert from '../../../components/ValidationAlert'
import { bindSetError, showServerFieldErrors } from '../../../utils/showServerFieldErrors'
import AttributesMultiSelect from '../../../components/FormElements/AttributesMultiSelect'
import { ATTRIBUTE_TYPES, READONLY_TOOLTIP } from '../../../consts'
import { LastUpdateMeta } from '../../../components/GenericModal/ChangeMetaInfo'
import EmailAliasesInput from '../../../components/FormElements/EmailAliasesInput'
import FormSwitch from '../../../components/FormElements/FormSwitch'
import { interpretRoleAssignment, isViewerFamilyRole } from '../../../helpers/roleHelpers'
import { type IAttribute } from '../../../models/TermsSection'
import { type ApplyFiltersAndDispatchParams } from '../../../components/CustomTable/types'
import NorthEastIcon from '@mui/icons-material/NorthEast'
import ROUTES from '../../../consts/paths'
import { PreparedRouteKeys } from '../../../hooks/useAllowedPath'
import { useUserProfile } from '../../../hooks/useUserProfile'
import { useAccess } from '../../../hooks/useAccess'
import { useUserRole } from '../../../hooks/useUserRole'
import { TabContext, TabList, TabPanel } from '@mui/lab'

interface RoleFormData {
  roleId: string
  academicUnitId: string
  /** `category_name` for scoped division search — not sent to role API */
  academicUnitCategoryName: string
  academicUnit?: TruncatedDivision
  role?: Role
  attributes?: IAttribute[]
}

interface FormData {
  firstName: string
  lastName: string
  studentId?: string
  email: string
  roles?: RoleFormData[]
  password?: string
  attributes?: any[]
  aliases?: string[]
  isCognitoProvisioned?: boolean
}

const AddRoleButtonStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '24px',
  padding: '24px',
  my: '24px',
  borderRadius: '8px',
  background: shade[750]
}

const RoleSchema = yup.object().shape({
  roleId: yup.string().required('Role is required'),
  academicUnitCategoryName: yup.string().required('Academic unit type is required'),
  academicUnitId: yup.string().required('Academic Unit is required'),
  attributes: yup.array().optional(),
  academicUnit: yup.mixed().optional(),
  role: yup.mixed().optional()
})

const facultyFormSchema = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Invalid email format').required('Email is required'),
  roles: yup.array().of(RoleSchema),
  password: yup.string().test('password', 'Password must be at least 6 characters',
    (password) => {
      if (!password) {
        return true
      }
      if (password.length < 6) {
        return false
      }
      return true
    }
  )
})

interface CreateAndEditFacultyAndStaffProps {
  selectedRow?: any
  selectedRowWithActions?: any
  handleCloseModal?: () => void
  params: ApplyFiltersAndDispatchParams
  isTaskRunning?: boolean
  onTaskBlock?: () => void
}

const CreateAndEditFacultyAndStaff: React.FC<CreateAndEditFacultyAndStaffProps> = ({ selectedRow, selectedRowWithActions, handleCloseModal, params: paramsForCDG, isTaskRunning, onTaskBlock }) => {
  const { readonly } = useAccess(selectedRowWithActions?.actions ?? [])
  const { activeRole, activeRoleId } = useUserRole()
  // Self-edit role mutations defer the page reload until the modal closes,
  // so the user can finish editing without losing the open form. The exception
  // is deleting the role they're currently signed in as — that has to reload
  // immediately so the rest of the UI re-resolves under the remaining roles.
  const needsReloadOnCloseRef = useRef(false)
  useEffect(() => {
    return () => {
      if (needsReloadOnCloseRef.current) {
        window.location.reload()
      }
    }
  }, [])
  const [systemAdmin, setSystemAdmin] = useState<boolean>(false)
  const [parsedRolesData, setParsedRolesData] = useState<RoleFormData[]>([])
  const [roleError, setRoleError] = useState<number | undefined>()
  const [deleteLoadingList, setDeleteLoadingList] = useState<number[]>([])
  const [errors, setErrors] = useState<unknown>({})
  const [currTab, setCurrTab] = useState('Details')
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const loading = actionLoading()
  const showCognitoToggle = activeRole?.roleType === 'GLOBAL'

  const { data: userProfile } = useUserProfile()
  const currentLoggedInUserId = userProfile?.user?.userId
  const params = {
    isFaculty: !systemAdmin,
    isAdmin: systemAdmin,
    sort: JSON.stringify({
      field: 'role.role_name',
      direction: 'asc'
    })
  }
  useEffect(() => {
    if (selectedRow != null && isTaskRunning) {
      onTaskBlock?.()
      handleCloseModal?.()
    }
  }, [selectedRow])

  const { data: selectRoles, isPending: selectRoleLoading, isFetching: rolesLoading } = useQuery({
    queryKey: ['faculty_roles', systemAdmin],
    enabled: !readonly,
    staleTime: 100,
    queryFn: async (): Promise<Role[]> => {
      const result = await api.get('/api/roles/', {
        params
      })
      return result.data
    }
  })

  const isEditAction = selectedRow !== undefined
  const { firstName, lastName, studentId, email, userId } = selectedRow ?? {}
  const defaultValues = useMemo<FormData>(() => ({
    firstName,
    lastName,
    studentId,
    email,
    roles: [],
    attributes: selectedRow?.attributes ?? [],
    aliases: selectedRow?.aliases ?? [],
    isCognitoProvisioned: selectedRow?.isCognitoProvisioned ?? false
  }), [firstName, lastName, studentId, email, selectedRow?.attributes, selectedRow?.aliases, selectedRow?.isCognitoProvisioned])

  const { isLoading: isRolesDataLoading, isFetching: isRolesDataFetching } = useQuery<DivisionAndSection[]>({
    enabled: isEditAction,
    queryKey: ['user_roles', userId],
    queryFn: async (): Promise<DivisionAndSection[]> => {
      const result = await api.get(`/api/users/${userId}/roles/`, {
        params
      })

      const data = result.data.filter((role: DivisionAndSection) => role?.role?.roleType === 'GENERIC')
      const newparsedRolesData = data.map(({ role, roleId, divisionId, division, attributes }: DivisionAndSection): RoleFormData => ({
        roleId,
        academicUnitId: divisionId ?? '',
        academicUnit: division,
        role,
        attributes,
        academicUnitCategoryName: division?.categoryName != null ? String(division.categoryName) : ''
      }))
      setParsedRolesData(newparsedRolesData)
      return data
    }
  })

  const methods = useForm<FormData>({
    resolver: yupResolver(facultyFormSchema) as Resolver<FormData>,
    defaultValues
  })

  const { mutateAsync, isPending: isRoleCreateLoading } = useMutation({
    meta: { silent: true },
    mutationFn: async (data: { userId: string }): Promise<any> => {
      const result = await api.post(`/api/users/${data?.userId}/role/`, data)
      return result.data
    }
  })

  const { handleSubmit, control, reset, getValues, formState: { isDirty, dirtyFields } } = methods

  useEffect(() => {
    reset(defaultValues)
  }, [reset, defaultValues])

  const { fields, prepend, remove } = useFieldArray({
    control,
    name: 'roles'
  })

  const onSubmit: SubmitHandler<FormData> = async (values: FormData) => {
    const isEmailInAliases = values.aliases?.includes(values.email)
    if (isEmailInAliases) {
      setErrors('Cannot use the same email in the Email and Email Aliases fields.')
      return
    }
    let iter = 0
    setRoleError(undefined)
    try {
      const { roles, password, ...userPayload } = getDirtyValues(dirtyFields, values)
      const cognitoPayload = showCognitoToggle ? { isCognitoProvisioned: values.isCognitoProvisioned ?? false } : {}
      const currentAttributes = getValues('attributes')
      const attributesPayload = Array.isArray(currentAttributes) ? currentAttributes : []

      const apiCallPayload = isEditAction
        ? { urlId: selectedRow.userId, data: { ...userPayload, ...cognitoPayload, attributes: attributesPayload, aliases: values.aliases } }
        : { data: { ...userPayload, ...cognitoPayload, attributes: attributesPayload, aliases: values.aliases, password: password === '' ? undefined : password } }
      const apiCall = isEditAction
        ? dispatch(UpdateFacultyAndStaffApi(apiCallPayload))
        : dispatch(createFacultyAndStaffApi(apiCallPayload))

      const res = await apiCall.unwrap()
      const userId = res?.userId
      const rolesList = values?.roles
      // Roles the user already held (BE returned 200 `already_exists`) — surfaced
      // as informational notices since the assignment was an idempotent no-op.
      const alreadyExistsMessages: string[] = []
      if (userId && rolesList) {
        // Resolve each row's roleId to its name so we can attach attributes to the
        // whole Viewer family (base "Viewer" plus CE-123 copies like "Viewer 1").
        const roleNameById = new Map(selectRoles?.map(role => [role.roleId, role.roleName]))
        // loop over all the roles that we recently added to a user and run the api one by one for each role
        for (const role of rolesList) {
          const payload = {
            divisionId: role.academicUnitId,
            roleId: role.roleId,
            userId,
            // only add for viewer-family roles (base "Viewer" and its copies)
            ...(isViewerFamilyRole(roleNameById.get(role.roleId)) ? { attributes: role.attributes } : {})
          }
          const result = await mutateAsync(payload).catch((e) => {
            // A 409 (Viewer already assigned to this unit with different
            // attributes) or 400 rejects here; rethrow so the outer catch shows
            // the BE guidance text as a blocking error.
            setRoleError(iter)
            throw e
          })
          const { alreadyExists, message } = interpretRoleAssignment(result)
          if (alreadyExists && message) {
            alreadyExistsMessages.push(message)
          }
          remove(0) // remove top item of form when processed
          setParsedRolesData(parsedRolesData.concat([role])) // add top item of form to parsedRolesData
          iter = iter++
        }
      }

      // Self-edits reload the page on modal close, which would tear down a
      // normal snackbar before it can be read — flash those so they survive the
      // reload. Otherwise persist them so they stick until the user dismisses.
      const willReloadOnClose = selectedRow?.userId === currentLoggedInUserId
      if (willReloadOnClose) {
        needsReloadOnCloseRef.current = true
      }
      const notify = (message: string, variant: 'success' | 'info', subTitle?: string): void => {
        if (willReloadOnClose) {
          flashNotification(message, { variant, subTitle })
        } else {
          notification(message, { variant, subTitle, persist: true })
        }
      }
      void dispatch(getFacultyAndStaffApi({ params: paramsForCDG }))
      handleCloseModal?.()
      notify(`Faculty ${isEditAction ? 'Updated' : 'Added'} Successfully!`, 'success', res.name)
      // A duplicate role assignment is not a failure, but it is not a fresh
      // create either — let the user know it already existed.
      alreadyExistsMessages.forEach((message) => {
        notify(message, 'info')
      })
    } catch (error) {
      // Field-keyed 422s name the field in the banner and highlight that input.
      // Modal stays open (Pattern A).
      setErrors(showServerFieldErrors(error, bindSetError(methods.setError), methods.clearErrors))
    }
  }

  const addRole = (): any => {
    prepend({ roleId: '', academicUnitId: '', academicUnitCategoryName: '', attributes: [] })
  }

  const deleteRole = async (role: RoleFormData, index: number): Promise<void> => {
    try {
      setDeleteLoadingList(deleteLoadingList.concat([index]))
      await api.delete(`/api/users/${userId}/role/${role.role?.roleId}?division_id=${role.academicUnit?.divisionId}`)

      const isSelfEdit = selectedRow?.userId === currentLoggedInUserId
      const isActiveRoleDelete = role.role?.roleId === activeRoleId

      if (isSelfEdit && isActiveRoleDelete) {
        // Removed the role this session is signed in with — reload now so
        // permissions/views re-resolve from the user's remaining roles.
        window.location.reload()
        return
      }

      setParsedRolesData(parsedRolesData.filter((_, i) => i !== index))
      notification('Role deleted Successfully!', { variant: 'success' })
      setDeleteLoadingList(deleteLoadingList.filter((_, i) => i !== index))
      void dispatch(getFacultyAndStaffApi({ params: paramsForCDG }))

      if (isSelfEdit) {
        needsReloadOnCloseRef.current = true
      }
    } catch (error: any) {
      notification(`Operation Failed: Failed to delete role; ${error?.response?.data}`, { variant: 'error' })
    }
  }

  const roleLoading = (!readonly && selectRoleLoading) || isRolesDataLoading

  const handleNavigateToPermissionManagement = (): void => {
    window.location.href = `${ROUTES.INSTITUTION_SETTINGS}?tab=${encodeURIComponent('System Level Role Management')}`
  }

  return (
    <FormProvider {...methods}>
      <ValidationAlert errors={errors} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <TabContext value={currTab}>
          <Box>
            <TabList onChange={(e, newValue) => { setCurrTab(newValue) }}>
              <Tab label="Details" value="Details" />
              <Tab label="Academic Unit Roles" value="Academic Unit Roles" />
              {showCognitoToggle && <Tab label="Advanced Settings" value="Advanced Settings" />}
            </TabList>
          </Box>
          <TabPanel value="Details">
            <Box display="flex" flexDirection="column" mt={2} gap={theme.spacing(6)}>
              <FormInput name="firstName" label="First name" placeholder="Enter first name" control={control} required />
              <FormInput name="lastName" label="Last name" placeholder="Enter last name" control={control} required />
              <FormInput name="studentId" label="School ID" placeholder="Enter a School ID" control={control} required />
              <FormInput name="email" label="Email" placeholder="Enter an email" control={control} required />
              {
                !isEditAction &&
                <FormInput name="password" type="password" label="Password" placeholder="Enter a password" control={control} />
              }

              <AttributesMultiSelect
                extraParams={{ attributeType: ATTRIBUTE_TYPES.FACULTY_COURSE_ATTRIBUTES }}
                name="attributes"
                defaultAttributes={selectedRow?.attributes}
                skip={readonly}
              />
              <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={600}>Additional Fields</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box display='flex' flexDirection='column' gap={4}>
                    <EmailAliasesInput name="aliases" defaultValue={selectedRow?.aliases} primaryEmail={selectedRow?.email} />
                    <Box>
                      <FormInput name="institutionId" defaultValue={selectedRow?.institutionId} label="Institution ID" control={control} placeholder='Institution ID' />
                      <Typography fontSize="14px" mt={1} color={grey[850]}>
                        Identifier from your SIS or data warehouse. Changing this may break syncing with your institutional data.
                      </Typography>
                    </Box>
                    <Box>
                      <FormInput name="integrationSourceId" defaultValue={selectedRow?.integrationSourceId} label="Integration Source ID" control={control} placeholder='Integration Source ID' />
                      <Typography fontSize="14px" mt={1} color={grey[850]}>
                        Identifier from an external system (e.g. LMS). Changing this may break the connection to that system and any related functionality.
                      </Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </TabPanel>
          <TabPanel value="Academic Unit Roles">
            {!roleLoading &&
              <Box sx={AddRoleButtonStyle}>
                <Tooltip title={readonly ? READONLY_TOOLTIP : ''} arrow disableHoverListener={!readonly}>
                  <span>
                    <GenericButton onClick={addRole} startIcon={<AddIcon />} label='Add role' buttonType={ButtonType.Secondary} fontWeight='600' disabled={readonly} />
                  </span>
                </Tooltip>
              </Box>}
            {(isRolesDataLoading || roleLoading || isRolesDataFetching) &&
              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '24px' }}>
                <CircularProgress />
              </Box>
            }
            {fields?.map((field, index) => (
              <RoleCard
                error={index === roleError}
                key={field.id}
                loading={roleLoading}
                readOnly={readonly}
                deleteDisabled={readonly}
                divisionFieldName={`roles[${index}].academicUnitId`}
                roleFieldName={`roles[${index}].roleId`}
                academicUnitCategoryFieldName={`roles[${index}].academicUnitCategoryName`}
                attributesFieldName={`roles[${index}].attributes`}
                valueFieldForAcademicUnit='divisionId'
                selectRoles={selectRoles}
                deleteRole={() => {
                  if (index === roleError) {
                    setRoleError(undefined)
                  }
                  remove(index)
                }} />
            ))}
            {parsedRolesData.map((role, index) => (
              <RoleCard
                key={`exist-${index}`}
                attributes={role?.attributes}
                selectedRole={role?.role}
                selectedDivision={role?.academicUnit}
                loading={roleLoading}
                divisionFieldName={`${index}existingdiv`}
                roleFieldName={`${index}existingrole`}
                selectRoles={selectRoles}
                deleteRole={() => {
                  void deleteRole(role, index)
                }}
                readOnly={true}
                deleteDisabled={readonly}
                deleteLoading={deleteLoadingList.includes(index)}
                valueFieldForAcademicUnit='divisionId'
                attributesFieldName={`parsedRolesData[${index}].attributes`}
              />
            ))}
          </TabPanel>
          {showCognitoToggle && (
            <TabPanel value="Advanced Settings">
              <Box sx={{ marginTop: 2 }}>
                {showCognitoToggle &&
                  <GenericButton
                    onClick={handleNavigateToPermissionManagement}
                    label='Add System Level Roles'
                    buttonType={ButtonType.Secondary}
                    buttonSize={ButtonSize.Small}
                    fontWeight='600'
                    endIcon={<NorthEastIcon />}
                  />
                }
              </Box>
              <Box mt={4}>
                <FormSwitch
                  name="isCognitoProvisioned"
                  label="Login via Classranked"
                  defaultValue={selectedRow?.isCognitoProvisioned ?? false}
                />
              </Box>
            </TabPanel>
          )}
        </TabContext>
        <Divider sx={{ my: '24px' }} />
        <DefaultModalActions
          destructiveButtonProps={{
            onClick: handleCloseModal
          }}
          confirmButtonProps={{
            text: isEditAction ? 'Save' : 'Create',
            type: 'submit',
            loading: loading || isRoleCreateLoading,
            disabled: readonly || (isEditAction && !isDirty),
            tooltip: readonly ? READONLY_TOOLTIP : undefined
          }}
          lastUpdateMeta={isEditAction && (
            <LastUpdateMeta lastEditedBy={selectedRow?.modifiedByUsername} lastEditedAt={selectedRow?.modifiedOn} />
          )}
        />
      </form>
    </FormProvider>
  )
}

export default CreateAndEditFacultyAndStaff
